import { attachmentCardFromRef } from "@components/AskAQuestionPrompt/resolveAttachmentPreview";
import { normalizeMentions } from "@components/AskAQuestionPrompt/mentionTokens";

const mapAttachmentRefs = (attachments) =>
  (Array.isArray(attachments) ? attachments : [])
    .map((attachment) => attachmentCardFromRef(attachment))
    .filter(Boolean);

/**
 * Maps GET /conversations/{access_id}/{conversation_id}/ JSON into iqChatStore messages.
 *
 * Primary shape:
 * `{ conversation: { messages: [{ message, source: 'user'|'llm', timestamp, attachments? }] } }`
 *
 * @param {object|null|undefined} detail
 * @returns {Array<{ id: string|number, type: 'user'|'ai', content: string|object, timestamp: Date }>}
 */
export function mapIQConversationDetailToChatMessages(detail) {
  if (!detail || typeof detail !== "object") return [];

  const root =
    detail.conversation && typeof detail.conversation === "object"
      ? detail.conversation
      : detail;

  const raw =
    root.messages ??
    detail.messages ??
    detail.conversation_messages ??
    detail.chat_history ??
    detail.history;

  if (!Array.isArray(raw)) return [];

  return raw
    .map((m, i) => {
      const source = (m.source ?? "").toString().toLowerCase();
      const legacyRole = (m.role || m.type || "").toString().toLowerCase();

      const text =
        m.message ?? m.content ?? m.text ?? m.question ?? "";

      const isUser =
        source === "user" ||
        legacyRole === "user" ||
        legacyRole === "human" ||
        legacyRole === "customer";

      if (isUser) {
        const attachmentRefs = mapAttachmentRefs(m.attachments);
        if ((text === "" || text == null) && attachmentRefs.length === 0) return null;
        const contentText =
          typeof text === "string" ? text : text == null ? "" : JSON.stringify(text);
        const mentions = normalizeMentions(m.mentions);
        return {
          id: m.id ?? `hist-u-${i}`,
          type: "user",
          content: contentText,
          format: "text",
          attachmentRefs,
          ...(mentions.length > 0 ? { mentions } : {}),
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
        };
      }

      const isAssistant =
        source === "llm" ||
        source === "assistant" ||
        source === "ai" ||
        source === "model" ||
        (!source &&
          (legacyRole === "assistant" ||
            legacyRole === "ai" ||
            legacyRole === "bot"));

      if (!isAssistant) {
        return null;
      }

      const aiPayload = m.content ?? m.response;
      let answer = "";

      if (typeof text === "string" && text !== "") {
        answer = text;
      } else if (typeof aiPayload === "string") {
        answer = aiPayload;
      } else if (aiPayload && typeof aiPayload === "object") {
        answer =
          aiPayload.answer ?? aiPayload.text ?? aiPayload.message ?? "";
      }

      const contentObj =
        aiPayload &&
        typeof aiPayload === "object" &&
        !Array.isArray(aiPayload) &&
        (aiPayload.answer !== undefined || aiPayload.sources !== undefined)
          ? {
              ...aiPayload,
              answer: answer || aiPayload.answer || "",
              sources: aiPayload.sources ?? [],
              version: aiPayload.version ?? "1.0.0",
            }
          : {
              answer: answer || "",
              sources: [],
              version: "1.0.0",
            };

      const answerStr =
        typeof contentObj.answer === "string"
          ? contentObj.answer
          : String(contentObj.answer ?? "");

      if (!answerStr.trim() && !contentObj.suggestions) {
        return null;
      }

      return {
        id: m.id ?? `hist-a-${i}`,
        type: "ai",
        content: contentObj,
        // LLM replies are markdown; ignore a persisted "text" format on assistant rows.
        format: "markdown",
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
      };
    })
    .filter(Boolean);
}
